import apiClient from './authClient';
import { API_ENDPOINTS } from './authConfig';
import type {
  LoginCredentials,
  RegisterData,
  User,
  ForgotPasswordData,
  ResetPasswordData,
  VerifyEmailData,
  BearerResponse,
  RegisterResponse,
  UserUpdate,
} from '../../types/auth';

export const authApi = {
  // Login user — JSON body to asab-identity
  login: async (credentials: LoginCredentials): Promise<BearerResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, {
      email: credentials.email || credentials.username,
      password: credentials.password,
    });

    const data = response.data;
    return {
      access_token: data.access_token,
      token_type: data.token_type || 'Bearer',
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
      user: data.user,
    };
  },

  // Register new user. Returns the full RegisterResponse so callers can
  // branch on pending_approval (closed-beta gate) vs the normal verify-email
  // flow. Older call sites that only care about the User can read .user.
  register: async (userData: RegisterData): Promise<RegisterResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.REGISTER, {
      email: userData.email,
      password: userData.password,
      full_name: userData.full_name,
    });

    const data = response.data as RegisterResponse;
    const rawUser = (data.user || (data as unknown as User)) as User;
    return { ...data, user: normalizeUser(rawUser) };
  },

  // Forgot password
  forgotPassword: async (data: ForgotPasswordData): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email: data.email });
  },

  // Reset password
  resetPassword: async (data: ResetPasswordData): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      token: data.token,
      new_password: data.new_password || data.password,
    });
  },

  // Verify email
  verifyEmail: async (data: VerifyEmailData): Promise<User> => {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
      token: data.token,
    });
    const result = response.data;
    return normalizeUser(result.user || result);
  },

  // Resend verification email
  requestVerifyToken: async (email: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, { email });
  },

  // Get current user profile (authenticated)
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get(API_ENDPOINTS.USERS.ME);
    return normalizeUser(response.data);
  },

  // Update current user profile (authenticated)
  updateCurrentUser: async (userData: Partial<UserUpdate>): Promise<User> => {
    const response = await apiClient.patch(API_ENDPOINTS.USERS.ME, userData);
    return normalizeUser(response.data);
  },

  // Refresh access token using refresh token
  refreshToken: async (refreshToken: string): Promise<BearerResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.REFRESH, {
      refresh_token: refreshToken,
    });

    const data = response.data;
    return {
      access_token: data.access_token,
      token_type: data.token_type || 'Bearer',
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
    };
  },

  // Logout — client-side only (clear token), no backend call needed
  logout: async (): Promise<void> => {
    // asab-identity doesn't have a logout endpoint.
    // Token invalidation happens via expiry or Keycloak session management.
    // Client just clears local state.
  },

  // Check authentication status
  checkAuthStatus: async (): Promise<{ isAuthenticated: boolean; user: User | null }> => {
    try {
      const userData = await apiClient.get(API_ENDPOINTS.USERS.ME);
      return {
        isAuthenticated: true,
        user: normalizeUser(userData.data),
      };
    } catch {
      return {
        isAuthenticated: false,
        user: null,
      };
    }
  },

  // Refresh user data
  refreshUserData: async (): Promise<User | null> => {
    try {
      const userData = await apiClient.get(API_ENDPOINTS.USERS.ME);
      return normalizeUser(userData.data);
    } catch {
      return null;
    }
  },

  // Validate token
  validateToken: async (token: string): Promise<boolean> => {
    try {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await apiClient.get(API_ENDPOINTS.USERS.ME);
      return true;
    } catch {
      return false;
    } finally {
      if (apiClient.defaults.headers.common) {
        delete apiClient.defaults.headers.common['Authorization'];
      }
    }
  },
};

// Normalize user object from asab-identity format to what the UI expects.
// asab-identity uses email_verified, the old UI code checks is_verified.
function normalizeUser(user: User | Record<string, unknown>): User {
  const u = user as Record<string, unknown>;
  return {
    ...u,
    is_verified: (u.email_verified as boolean) ?? (u.is_verified as boolean) ?? false,
    is_active: (u.is_active as boolean) ?? true,
    is_superuser: (u.is_superuser as boolean) ?? false,
  } as User;
}
